import { Box, Typography } from "@mui/material";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import {
  useChatStore,
  type Dataset as ChatStoreDataset,
} from "src/views/assistant/stores/ChatStore";
import LayersIcon from "@mui/icons-material/Layers";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AttachmentsContainer from "src/reusable/Chat/PromptInput/AttachmentsContainer";
import SelectedDatasetsContainer from "src/reusable/Chat/PromptInput/SelectedDatasetsContainer";
import {
  removeWorkflowInputFile,
  useWorkflowInputFilesDisplay,
} from "src/views/products/workflow/hooks/useWorkflowInputFilesDisplay";

/**
 * RFPViewer - Displays selected/uploaded files and datasets for proposal generation
 *
 * This component reads from:
 * - ProposalGenerationStore.inputFiles - uploaded and selected files
 * - ChatStore.dropdownDatasets - RFP datasets added for search
 *
 * It provides remove functionality by updating the respective stores.
 */
const RFPViewer = () => {
  const { inputFiles, setInputFiles, removeUploadedFile } =
    useProposalGenerationStore();
  const { dropdownDatasets: rfpDatasets, setDropdownDatasets: setRfpDatasets } =
    useChatStore();

  const { displayFiles, pendingArtifactFileIds, uploadError } =
    useWorkflowInputFilesDisplay(inputFiles);

  // Shared removal, plus PG's own bookkeeping: keep ChatStore's
  // sourceDataRecordIds in step with what remains staged.
  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = removeWorkflowInputFile(
      {
        inputFiles,
        selectedArtifacts:
          useProposalGenerationStore.getState().selectedArtifacts,
        setInputFiles,
        removeSelectedArtifact:
          useProposalGenerationStore.getState().removeSelectedArtifact,
        removeUploadedFile,
      },
      fileId,
    );
    const { setSourceDataRecordIds } = useChatStore.getState();
    setSourceDataRecordIds(
      updatedFiles
        .filter((file) => file.sdrId)
        .map((file) => file.sdrId as number),
    );
  };

  // Handler for removing an RFP dataset
  const handleRemoveRfpDataset = (datasetId: number) => {
    const updatedDatasets = rfpDatasets.filter(
      (d: ChatStoreDataset) => d.id !== datasetId,
    );
    setRfpDatasets(updatedDatasets);
  };

  // Don't render anything if there are no files or datasets
  if (rfpDatasets.length === 0 && displayFiles.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "action.hover",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 18, color: "text.secondary" }}
        />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Selected Data
        </Typography>
      </Box>

      {/* RFP Datasets Section - Shows datasets added to ChatStore */}
      {rfpDatasets.length > 0 && (
        <SelectedDatasetsContainer
          datasets={rfpDatasets}
          onRemove={handleRemoveRfpDataset}
          icon={<LayersIcon fontSize="small" color="primary" />}
        />
      )}

      {/* Show selected/uploaded files */}
      {displayFiles.length > 0 && (
        <AttachmentsContainer
          processedFiles={displayFiles}
          removeFile={handleRemoveFile}
          isDragActive={false}
          uploadError={uploadError}
          pendingArtifactFileIds={pendingArtifactFileIds}
        />
      )}
    </Box>
  );
};

export default RFPViewer;
